import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Copy, Trash2, Edit, Loader2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { templateApi } from "@/lib/api";
import { STARTER_TEMPLATES } from "@/lib/starterTemplates";
import { generateId, SECTION_ORDER } from "@/lib/elements";
import { ElementContent } from "@/components/designer/ElementRenderer";

function TemplatePreview({ sections }) {
  const allElements = sections
    ? SECTION_ORDER.flatMap((key) => sections[key] || [])
    : [];
  if (allElements.length === 0) return null;
  return (
    <div className="h-40 bg-gray-50/80 overflow-hidden relative border-b border-gray-100">
      <div
        style={{
          width: 794,
          minHeight: 400,
          transform: "scale(0.19)",
          transformOrigin: "top left",
          padding: "15mm",
          backgroundColor: "white",
          position: "absolute",
          top: 4,
          left: 4,
        }}
      >
        {allElements.slice(0, 8).map((el) => (
          <ElementContent key={el.id} element={el} />
        ))}
      </div>
    </div>
  );
}

function countElements(template) {
  if (template.sections) {
    return SECTION_ORDER.reduce(
      (sum, key) => sum + (template.sections[key] || []).length,
      0
    );
  }
  return (template.elements || []).length;
}

function getPreviewSections(template) {
  if (template.sections) return template.sections;
  if (template.elements) {
    return {
      reportHeader: [],
      pageHeader: [],
      body: template.elements,
      pageFooter: [],
      reportFooter: [],
    };
  }
  return null;
}

function StarterCard({ template, onUse }) {
  return (
    <Card
      data-testid={`starter-template-${template.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-gray-200 hover:border-gray-300 overflow-hidden"
    >
      <div className="h-1.5" style={{ backgroundColor: template.color }} />
      <TemplatePreview sections={template.sections} />
      <CardContent className="p-5">
        <h3 className="font-heading font-bold text-base text-gray-900 mb-1">
          {template.name}
        </h3>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
          {template.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {countElements(template)} elements
          </span>
          <Button
            size="sm"
            data-testid={`use-template-${template.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onUse(template)}
            className="gap-1.5 h-8 text-xs"
          >
            <Plus className="w-3 h-3" /> Use Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SavedCard({ template, onEdit, onDuplicate, onDelete }) {
  const updatedDate = template.updatedAt
    ? new Date(template.updatedAt).toLocaleDateString()
    : "";

  return (
    <Card
      data-testid={`saved-template-${template.id}`}
      className="group hover:shadow-lg transition-all duration-200 border-gray-200 hover:border-gray-300 overflow-hidden"
    >
      <TemplatePreview sections={getPreviewSections(template)} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-base text-gray-900 truncate">
              {template.name}
            </h3>
            {template.description && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {template.description}
              </p>
            )}
          </div>
          <FileText className="w-4 h-4 text-gray-300 shrink-0 ml-2" />
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-400">
            {updatedDate} &middot; {countElements(template)} elements
          </span>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              data-testid={`edit-template-${template.id}`}
              onClick={() => onEdit(template)}
            >
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              data-testid={`duplicate-template-${template.id}`}
              onClick={() => onDuplicate(template)}
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-red-500 hover:text-red-600"
              data-testid={`delete-template-${template.id}`}
              onClick={() => onDelete(template)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TemplatesPage() {
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchTemplates = () => {
    templateApi
      .list()
      .then(setSavedTemplates)
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleUseStarter = async (starter) => {
    try {
      const newSections = {};
      for (const key of SECTION_ORDER) {
        newSections[key] = (starter.sections[key] || []).map((el) => ({
          ...el,
          id: generateId(),
        }));
      }
      const created = await templateApi.create({
        name: starter.name,
        description: starter.description,
        sections: newSections,
        pageSettings: starter.pageSettings,
      });
      navigate(`/designer/${created.id}`);
    } catch {
      toast.error("Failed to create template");
    }
  };

  const handleDelete = async (template) => {
    try {
      await templateApi.delete(template.id);
      setSavedTemplates((prev) => prev.filter((t) => t.id !== template.id));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const dup = await templateApi.duplicate(template.id);
      setSavedTemplates((prev) => [...prev, dup]);
      toast.success("Template duplicated");
    } catch {
      toast.error("Failed to duplicate template");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="templates-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-gray-900 tracking-tight">
                Invoice Designer
              </h1>
              <p className="text-sm text-gray-500 mt-2 max-w-lg">
                Create drag-and-drop invoice templates that export to EJS for
                jsreport. No manual HTML coding needed.
              </p>
            </div>
            <Button
              data-testid="configure-elements-btn"
              variant="outline"
              onClick={() => navigate("/configure")}
              className="gap-2 h-10 px-5"
            >
              <Settings className="w-4 h-4" /> Configure Elements
            </Button>
            <Button
              data-testid="create-new-template-btn"
              onClick={() => navigate("/designer")}
              className="gap-2 h-10 px-5"
            >
              <Plus className="w-4 h-4" /> New Template
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        <section>
          <h2 className="font-heading font-bold text-lg text-gray-800 mb-1">
            Starter Templates
          </h2>
          <p className="text-xs text-gray-400 mb-5">
            Pre-built invoice layouts to get you started quickly
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STARTER_TEMPLATES.map((t) => (
              <StarterCard key={t.name} template={t} onUse={handleUseStarter} />
            ))}
          </div>
        </section>

        <Separator />

        <section>
          <h2 className="font-heading font-bold text-lg text-gray-800 mb-1">
            Your Templates
          </h2>
          <p className="text-xs text-gray-400 mb-5">
            Templates you&apos;ve created and saved
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : savedTemplates.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">
                No saved templates yet. Create one or use a starter template.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {savedTemplates.map((t) => (
                <SavedCard
                  key={t.id}
                  template={t}
                  onEdit={(tpl) => navigate(`/designer/${tpl.id}`)}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
